import {Avatar, message, Modal} from 'antd'
import {course_getCourseSubjects_Api} from 'apis'
import {getId} from 'apis/model/base'
import EmptyHolder from 'components/EmptyHolder'
import getTitle from 'helpers/getTitle'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {withProps} from 'recompose'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import {NullAsDefault, renderElse, renderIf} from 'views/Shared'
import CRUD from 'views/Shared/enums/CRUD'
import {SlateRenderer} from 'views/SlateEditor'
import {convertFromString, isEmptyContent} from 'views/SlateEditor/functions'
import {FetchSubjectById} from '../../components/FetchEntity'
import CourseContext from '../../CourseContext'
import {HeaderWithIconLine} from '../../../Templates/items/ListHeader'
import {RiSubscript} from 'react-icons/ri'
import {GiIronCross} from 'react-icons/gi'
import {TbListDetails} from 'react-icons/tb'
import {BsInfoCircleFill} from 'react-icons/bs'
import {ItemInformation} from 'components/common/ItemInformation'
import LoadingBlock from '../../../../Loading/LoadingBlock'
import useAsync from '../../../../../modules/asyncCache/useAsync'
import _ from 'lodash'
import {getResponseItem} from '../../../../Shared'
import copyToClipboard from "copy-to-clipboard";
import {IoCopyOutline} from "react-icons/io5";
import {FiChevronsRight} from "react-icons/fi";

const renderItem = ({renderActions = NullAsDefault, handleAction = NullAsDefault} = {}) => (item, index) => {
    const {subject, number_of_sessions, number_of_credits, is_mandatory} = item ?? {}
    const {description, subject_idcode} = subject ?? {}

    return (
        <Translate key={index}>
            {(t) => (
                <div
                    onClick={() => handleAction(CRUD.READ, item)}
                    className="p-3 background rounded-xl border border-color-50 shadow-sm hover:shadow-md relative cursor-pointer transition-all">
                    <div className="flex flex-col">
                        <h2 className="font-bold text-base text-gray-800 tracking-wide">
                            {getTitle(subject)}
                        </h2>
                        <div className="flex flex-col md:flex-row md:items-center gap-x-6 gap-y-1">
                            <ItemInformation
                                showIcon={false}
                                label="subject code"
                                value={
                                    <span
                                        onClick={() => {
                                            copyToClipboard(subject_idcode)
                                            message.success(t('copied'))
                                        }}
                                        className="font-mono font-semibold text-secondary hover:text-secondary-600 cursor-pointer">
                                            {subject_idcode}
                                        <IoCopyOutline size={12} className="ml-1"/>
                                        </span>
                                }
                                className="tracking-wider"
                            />
                            {renderIf(
                                is_mandatory === 1,
                                <span className="text-primary-400 italic tracking-wider">
                                    {`* ${t('is mandatory subject')}`}
                                </span>
                            )}
                        </div>
                        <div className="my-2 border-b border-color-50"/>
                        <div className="flex flex-col md:flex-row md:items-center gap-x-6 gap-y-1">
                            <ItemInformation
                                showIcon={true} label="number of sessions" className="italic tracking-wider"
                                value={`${number_of_sessions} ${t('sessions')}`}
                            />
                            <ItemInformation
                                showIcon={true} label="number of credits" className="text-xs tracking-wider"
                                value={`${number_of_credits} ${t('credits')}`}
                            />
                        </div>
                        {renderIf(
                            description,
                            <div className="mt-0.5 text-color-300 max-lines-3 tracking-wider">
                                {description}
                            </div>
                        )}
                    </div>
                    <div className="mt-1 flex justify-end">
                        {renderActions(item)}
                    </div>
                </div>
            )}
        </Translate>
    )
}

const DetailModalContent = ({item}) => {
    const t = useTranslate()
    const {
        about,
        description,
        number_of_sessions,
        subject_idcode,
    } = item ?? {}

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
                <Avatar
                    size={50}
                    icon={<RiSubscript size={26} className="text-primary-500"/>}
                    className="flex flex-center bg-primary-50 rounded-lg"
                />
                <div className="flex flex-col">
                    <span className="font-bold text-lg text-color-000">
                        {getTitle(item)}
                    </span>
                    <ItemInformation label="subject code" value={`#${subject_idcode}`} className="text-xs italic"/>
                </div>
            </div>
            {description && (
                <div className="text-color-300 tracking-wider">{description}</div>
            )}
            <div className="flex flex-col gap-2">
                <HeaderWithIconLine
                    icon={<BsInfoCircleFill size={14} className="text-blue-400 mr-1"/>}
                    title="subject information"
                    className="text-sm text-color-400"
                />
                <div className="ml-0 md:ml-1 flex flex-col flex-1 gap-1">
                    <ItemInformation
                        showIcon={true}
                        label="number of sessions"
                        value={`${number_of_sessions} ${t('sessions')}`}
                        className="text-sm"
                    />
                    {renderElse(
                        isEmptyContent(about),
                        <div className="flex flex-col">
                            <span className="text-sm text-color-500 italic">
                                {`${t('introduce')} : `}
                            </span>
                            <SlateRenderer value={convertFromString(about)}/>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

const useCourseSubjects = ({course}) => {
    const t = useTranslate()
    const {edit} = course ?? {}
    const course_id = getId(course)

    const [action, setAction] = useState({type: undefined, value: undefined})

    const onCancel = () => setAction({})

    const modal = useMemo(() => {
        const {id} = action?.value ?? {}
        let Content = NullAsDefault
        let Wrapper = ({children}) => children({item: {}})
        let params = {}
        let modal_params = {}
        switch (action?.type) {
            case CRUD.READ:
                modal_params = {
                    width: 600,
                    footer: undefined,
                    cancelText: t('close'),
                    okButtonProps: {hidden: true},
                    cancelButtonProps: {className: 'text-color-200 button-rounded-lg no-border'},
                    title: (
                        <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                            {t('subjects')}
                        </div>
                    ),
                }
                params = {item: action?.value}
                Content = DetailModalContent
                Wrapper = withProps({id})(FetchSubjectById)
                break
            default:
                break
        }
        return (
            <Modal
                footer={null}
                onCancel={onCancel}
                destroyOnHidden={true}
                open={action?.type}
                className="custom-modal"
                {...modal_params}>
                <Wrapper>
                    {({item, isLoading}) => {
                        if (!!isLoading) return <LoadingBlock/>
                        return <Content {...params} {...{item: action?.value, onCancel, course_id}}/>
                    }}
                </Wrapper>
            </Modal>
        )
    }, [action, course_id])

    const renderActions = useCallback(
        (item) => (
            <div
                onClick={(e) => {
                    e.stopPropagation()
                    setAction({type: CRUD.READ, value: item?.subject})
                }}
                className="flex items-center text-xs text-color-400 hover:text-secondary-600 italic cursor-pointer">
                {t('quick view')}
                <FiChevronsRight size={13} className="ml-1 my-auto"/>
            </div>
        ),
        []
    )

    return {modal, action, onCancel, setAction, course_id, renderActions}
}

const SubjectList = ({item, ...props}) => {
    const t = useTranslate()
    const course_id = getId(item)

    const apiInfo = course_getCourseSubjects_Api

    const [action, setAction] = useState({type: undefined, value: undefined})

    const {response, isLoading = false} = useAsync({
        apiInfo,
        query: bindQueryParam({id: course_id}),
    })

    const cache = getResponseItem(response)
    const data = _.get(cache, 'data', [])
    const groupedData = _.groupBy(data, 'group_name')
    const groupedDataKeysRaw = Object.keys(groupedData)
    const nullElements = _.filter(groupedDataKeysRaw, (e) => e === null || e === 'null')
    const nonNullElements = _.filter(groupedDataKeysRaw, (e) => e !== null && e !== 'null')
    const sortedNonNullElements = _.sortBy(nonNullElements, [(e) => e.toString().toLowerCase()])
    const groupedDataKeys = [...nullElements, ...sortedNonNullElements]

    const {modal, renderActions} = useCourseSubjects({course: item})

    if (isLoading) return <LoadingBlock/>

    if (data.length <= 0) {
        return <EmptyHolder style={{border: 'none'}}/>
    }

    return (
        <>
            <div className="flex flex-col gap-6">
                {groupedDataKeys.map((groupKey, i) => (
                    <div key={i} className="flex flex-col gap-4">
                        {renderIf(
                            groupKey !== 'null' && groupKey !== null && groupedDataKeys.length > 1,
                            <div
                                style={{
                                    padding: '2px 8px',
                                    borderRadius: 4,
                                    background: 'linear-gradient(to right, var(--background-200), rgba(0,0,0,0) 100%)',
                                }}
                                className="flex items-center gap-1">
                                <TbListDetails size={13} className="text-color-300"/>
                                <span className="font-semibold text-xs text-color-300 uppercase tracking-wider">
                                    {groupKey}
                                </span>
                            </div>
                        )}
                        <div className="flex flex-col gap-4">
                            {_.sortBy(_.get(groupedData, groupKey, []), ['subject.title']).map((ee, ii) =>
                                renderItem({
                                    renderActions,
                                    handleAction: (type, value) => setAction({type, value}),
                                })(ee, ii)
                            )}
                        </div>
                    </div>
                ))}
            </div>
            {modal}
        </>
    )
}

const CourseSubjectTab = (props) => {
    const {item} = useContext(CourseContext)
    return <SubjectList item={item} {...props}/>
}

export const CourseSubjectShortList = (props) => {
    const {item} = useContext(CourseContext)
    return <SubjectList item={item} sample={2} {...props}/>
}

export const CourseSubjectsInfo = ({course}) => {
    const subjects = Array.from(course?.subjects ?? [])
    const {modal, renderActions} = useCourseSubjects({course})
    return (
        <div className="flex flex-col gap-3">
            {subjects.length > 0
                ? subjects.map(renderItem({renderActions}))
                : <EmptyHolder/>
            }
            {modal}
        </div>
    )
}

export default CourseSubjectTab
